import { useState, useEffect } from "react";
import { FaComment, FaRegComment, FaTimes } from "react-icons/fa";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Spinner } from "reactstrap";
import config from '../../config.js';
import { useAuth0 } from "@auth0/auth0-react";
import './Comments.css';

const Comments = ({ quillId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingPost, setLoadingPost] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect, user } = useAuth0();

  const toggleModal = () => setIsOpen(!isOpen);

  useEffect(() => {
    if (quillId) fetchComments();
  }, [quillId]);

  const fetchComments = async () => {
    try {
      let token = null;
      if (isAuthenticated) {
        try {
          token = await getAccessTokenSilently();
        } catch {
          // ignore token error for fetching comments
        }
      }

      const response = await fetch(`${config.apiUrl}/${config.apiStage}/comments?quill_id=${quillId}`, {
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      const data = await response.json();
      setComments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const handlePostComment = async () => {
    if (!newComment.trim()) return;

    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    setLoadingPost(true);

    let token;
    try {
      token = await getAccessTokenSilently();
    } catch {
      const currentPath = window.location.pathname;
      return loginWithRedirect({
        redirectUri: `${window.location.origin}/callback?redirectTo=${encodeURIComponent(currentPath)}`,
      });
    }

    try {
      await fetch(`${config.apiUrl}/${config.apiStage}/comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ quill_id: quillId, content: newComment }),
      });

      setNewComment("");
      fetchComments();
    } catch (error) {
      console.error("Error posting comment:", error);
    } finally {
      setLoadingPost(false);
    }
  };

  const handleDeleteComment = (commentId) => {
    setShowDeleteModal(true);
    setCommentToDelete(commentId);
  };

  const confirmDeleteComment = async () => {
    if (!commentToDelete) return;

    setLoadingDelete(true);

    let token;
    try {
      token = await getAccessTokenSilently();
    } catch {
      const currentPath = window.location.pathname;
      return loginWithRedirect({
        redirectUri: `${window.location.origin}/callback?redirectTo=${encodeURIComponent(currentPath)}`,
      });
    }

    try {
      await fetch(`${config.apiUrl}/${config.apiStage}/comment`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ comment_id: commentToDelete }),
      });

      fetchComments();
      setShowDeleteModal(false);
      setCommentToDelete(null);
    } catch (error) {
      console.error("Error deleting comment:", error);
    } finally {
      setLoadingDelete(false);
    }
  };

  const hasUserCommented = comments.some(c => c.username && c.username === user?.username);

  const handleComment = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    setIsOpen(true);
  };

  const handleLoginRedirect = () => {
    const currentPath = window.location.pathname;
    loginWithRedirect({
      redirectUri: `${window.location.origin}/callback?redirectTo=${encodeURIComponent(currentPath)}`,
    });
  };

  const handleCloseLoginModal = () => setShowLoginModal(false);
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setCommentToDelete(null);
  };

  return (
    <>
      <button onClick={handleComment} className="flex items-center ml-2" style={{ width: '70px' }}>
        {hasUserCommented ? (
          <FaComment size={24} color="black" />
        ) : (
          <FaRegComment size={24} color="gray" />
        )}
        <span className="text-black">{comments.length}</span>
      </button>

      {/* Comments Modal */}
      <Modal isOpen={isOpen} toggle={toggleModal} className="modal-bottom">
        <ModalHeader toggle={toggleModal} className="text-black">Comments</ModalHeader>
        <ModalBody className="comments-list text-black">
          {comments.length === 0 ? (
            <p className="text-black">No comments yet. Be the first to comment!</p>
          ) : (
            comments.map((c, i) => (
              <div key={i} className="comment-item text-black py-2">
                <strong>{c.username || 'Anonymous'}:</strong> {c.content}
                {user?.username === c.username && (
                  <Button
                    color="danger"
                    size="sm"
                    style={{ float: 'right', padding: '5px' }}
                    onClick={() => handleDeleteComment(c.comment_id)}
                  >
                    {loadingDelete ? <Spinner size="sm" /> : <FaTimes />}
                  </Button>
                )}
              </div>
            ))
          )}
        </ModalBody>
        <ModalFooter className="comment-footer modal-footer">
          <Input
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write a comment..."
          />
          <Button color="primary" onClick={handlePostComment}>
            {loadingPost ? <Spinner size="sm" /> : 'Post'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Custom Login Modal */}
      <Modal isOpen={showLoginModal} toggle={handleCloseLoginModal} className="modal-bottom text-black">
        <ModalHeader toggle={handleCloseLoginModal} className="text-black">Login Required</ModalHeader>
        <ModalBody className="text-black">
          You need to log in to post a comment. Do you want to log in now?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseLoginModal}>Cancel</Button>
          <Button color="primary" onClick={handleLoginRedirect}>Log In</Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} toggle={handleCloseDeleteModal} className="modal-bottom text-black">
        <ModalHeader toggle={handleCloseDeleteModal} className="text-black">Delete Comment</ModalHeader>
        <ModalBody className="text-black">Are you sure you want to delete this comment?</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseDeleteModal}>Cancel</Button>
          <Button color="danger" onClick={confirmDeleteComment}>
            {loadingDelete ? <Spinner size="sm" /> : 'Delete'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Comments;
